import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import log from '@/services/logger/log';
import notificationPermissionService from '@/services/pushNotification/notificationPermissionService';
import pushNotificationService from '@/services/pushNotification/pushNotificationService';
import subscriptionService from '@/services/pushNotification/subscriptionService';
import notificationStorageService from '@/services/storage/notificationStorageService';
import userBrowserService from '@/services/user/userBrowserService';
import {selectIsLoggedIn} from '@/state/selectors/auth';
import {
    selectPushNotificationIsSettingChangeInProgress,
    selectPushNotificationSubscription,
} from '@/state/selectors/pushNotification';
import {setPushNotificationSubscription} from '@/state/slices/pushNotificationSlice';
import {getState} from '@/state/store';

import useOnFocusChange from './useOnFocusChange';

const usePushNotificationSubscriptionManager = () => {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const getSubscription = () => subscriptionService.getStoredSubscriptionInfo();

    const handleSubscriptionManagement = async () => {
        try {
            if (!isLoggedIn) return;
            if (!pushNotificationService.checkIfPushNotificationSupported()) return;

            const isSettingChangeInProgress = selectPushNotificationIsSettingChangeInProgress(getState());

            // If setting change is in progress, do not manage subscription
            if (isSettingChangeInProgress) return;

            const isPushNotificationDisabled = notificationStorageService.getIsPushNotificationDisabled();
            const isNotificationGranted = await notificationPermissionService.checkIfNotificationIsGranted();
            let subscriptionFromStorage = getSubscription();
            const currentSubscription = await subscriptionService.getCurrentSubscription();
            const subscriptionFromState = selectPushNotificationSubscription(getState());

            // Sync current subscription with state
            if (currentSubscription && !subscriptionFromState) {
                dispatch(setPushNotificationSubscription(currentSubscription));
            } else if (!currentSubscription && subscriptionFromState) {
                dispatch(setPushNotificationSubscription(null));
            }

            const isPermissionDenied = await notificationPermissionService.checkIfNotificationIsDenied();
            const isPermissionDefault = await notificationPermissionService.checkIfNotificationIsDefault();
            const isIosDevice = userBrowserService.isIosDevice();
            const isPushNotificationsTurnedOff =
                isPermissionDenied || (isPermissionDefault && !isIosDevice) || isPushNotificationDisabled;

            // If permission is
            // denied or
            // (default and not ios)
            //    it is impossible to set default state on ios,
            //    so this check is used to get around a problem that on older ios versions 'prompt' status is returned even if it is 'granted'
            // and
            // there's a subscriptionId
            // delete subscription
            if (isPushNotificationsTurnedOff && subscriptionFromStorage?.subscriptionId) {
                log.debug(
                    'SubscriptionManager: Push notifications are turned off and subscription exists, deleting subscription'
                );
                await subscriptionService.deleteSubscriptionFromBackend();
                dispatch(setPushNotificationSubscription(null));
            }

            // If push notifications are turned off
            // don't do anything
            if (isPushNotificationsTurnedOff) return;

            // If notification is granted and subscription exists but doesn't match current subscription
            // eg change provider from native to firebase
            // delete subscription
            if (isNotificationGranted && currentSubscription && subscriptionFromStorage?.data) {
                const valueFromStorage =
                    subscriptionFromStorage?.data?.token || subscriptionFromStorage?.data?.endpoint;
                const valueFromCurrentSubscription = currentSubscription.token || currentSubscription.endpoint;

                if (valueFromStorage !== valueFromCurrentSubscription) {
                    // If current subscription exists, sync it to backend first
                    if (valueFromCurrentSubscription) {
                        log.debug(
                            'SubscriptionManager: syncing current subscription to backend first (prevent data loss)'
                        );
                        await subscriptionService.storeSubscriptionToBackend(currentSubscription);
                        dispatch(setPushNotificationSubscription(currentSubscription));
                    }

                    log.debug('SubscriptionManager: tokens/endpoints do not match, deleting old subscription');
                    await subscriptionService.deleteSubscriptionFromBackend(subscriptionFromStorage);
                    subscriptionFromStorage = getSubscription();
                }
            }

            // If notification is granted and there is a current subscription but no stored subscriptionData, sync it
            if (isNotificationGranted && currentSubscription && !subscriptionFromStorage?.data) {
                log.debug('SubscriptionManager: syncing current subscription to backend (no stored data)');
                await subscriptionService.storeSubscriptionToBackend(currentSubscription);
                dispatch(setPushNotificationSubscription(currentSubscription));
            }

            // If notification is granted push notifications are not disabled, and there is no subscription
            // get subscription
            if (isNotificationGranted && !isPushNotificationDisabled && !currentSubscription) {
                log.debug('SubscriptionManager: creating new push notification subscription');
                await pushNotificationService.subscribe();
            }
        } catch (e) {
            log.debug(`SubscriptionManager: error: ${e}`);
        }
    };

    useEffect(() => {
        handleSubscriptionManagement();
    }, [isLoggedIn]);

    useOnFocusChange(handleSubscriptionManagement);
};

export default usePushNotificationSubscriptionManager;
